import { TableCell } from '@mui/material';
import styled from 'styled-components';
import { Typography, Button, TableWrapperHeader, SimpleTable } from '../../design-system';
import { DashboardHeader } from '../dashboard';
import { formatContainerType, momentTextFormat, showToRoles } from '../../utils';
import { getCache, saveCache } from '../../services';
import { StoragePricingGetOneResponse, StoragePricingUpdateModalCache, User } from '../../types';
import { CURRENCIES } from '../../const';
import { i18n } from '../../i18n';

const StoragePricingTableWrapperHeader = styled(TableWrapperHeader)`
  & > *:nth-child(2) {
    position: sticky;
    right: 0;
  }
`;

interface StoragePricingsViewHeaderProps {
  isFirstIndex: boolean;
}

export const StoragePricingsViewHeader = ({ isFirstIndex }: StoragePricingsViewHeaderProps) => {
  return (
    <DashboardHeader>
      <span />
      {isFirstIndex && (
        <Button size={'medium'} data-color={'white'} to={-1}>
          {i18n.t('button.goBack')}
        </Button>
      )}
    </DashboardHeader>
  );
};

interface StoragePricingsTableProps {
  data: StoragePricingGetOneResponse['data'];
  pricing: StoragePricingGetOneResponse['data']['pricing'][number];
  user: User;
  refetch: () => void;
}

export const StoragePricingsTable = ({
  data,
  pricing,
  user,
  refetch,
}: StoragePricingsTableProps) => {
  const handleUpdateClick = () => {
    saveCache<StoragePricingUpdateModalCache>('storage-pricing-update-modal', (props) => ({
      ...props,
      id: data.id,
      clientYard: data.clientYard,
      activePricing: pricing,
      refetch,
    }));

    getCache<StoragePricingUpdateModalCache>('storage-pricing-update-modal').open();
  };

  const symbol = CURRENCIES[pricing.currencyCode].symbol;
  const tableData = {
    data: pricing.containers,
    totalCount: pricing.containers.length,
  };

  return (
    <SimpleTable
      data={tableData}
      filters={() => (
        <StoragePricingTableWrapperHeader align={'right'}>
          <Typography variant={'textL'} weight={'bold'}>
            {i18n.t('label.effectiveDate')}: {momentTextFormat(pricing.activationDate, user)} |{' '}
            {i18n.t('label.freeDays')}: {pricing.freeDays}
          </Typography>
          {showToRoles(['SUPER_ADMIN', 'YARD_COMPANY_ADMIN', 'YARD_COMPANY_EMPLOYEE'], user) && (
            <Button size={'medium'} onClick={handleUpdateClick}>
              {i18n.t('button.update')}
            </Button>
          )}
        </StoragePricingTableWrapperHeader>
      )}
      tableRowProps={(row, i) => ({
        key: 'storage-pricing-table-row' + i,
        onClick: () => {},
      })}
      columns={[
        {
          headText: () => i18n.t('label.type'),
          body: (row) => (
            <TableCell component={'th'} align={'left'}>
              {formatContainerType(row.type)}
            </TableCell>
          ),
        },
        {
          headText: () => i18n.t('label.loaded'),
          bodyText: (row) => row.loaded + symbol,
        },
        {
          headText: () => i18n.t('label.empty'),
          bodyText: (row) => row.empty + symbol,
        },
        {
          headText: () => i18n.t('label.craneFeeLoadedIn'),
          bodyText: (row) => row.loadedCraneIn + symbol,
        },
        {
          headText: () => i18n.t('label.craneFeeLoadedOut'),
          bodyText: (row) => row.loadedCraneOut + symbol,
        },
        {
          headText: () => i18n.t('label.craneFeeEmptyIn'),
          bodyText: (row) => row.emptyCraneIn + symbol,
        },
        {
          headText: () => i18n.t('label.craneFeeEmptyOut'),
          bodyText: (row) => row.emptyCraneOut + symbol,
        },
      ]}
    />
  );
};
